import { DocumentType } from "encamp-prisma/generated/browser";

import { FeatureFlagKey, FeatureFlagMap } from "../featureFlags";

/**
 * Which documents each feature admits to the Document AI pipeline, and the one
 * rule that answers "may this document be enriched for this tenant".
 *
 * The pipeline's document query and the callers that dispatch to it both need
 * this answer, and when they derive it separately they drift — a request gets
 * authorized by one and dropped by the other, and a job that selects nothing
 * still reports success. Both read it from here, against flags resolved by
 * `getEffectiveFlagsForTenant`.
 */

/**
 * Document-derived manifests: human uploads and EPA printed-copy scans. The
 * printed-copy type is admitted so an explicit parse request can select it; the
 * nightly sweep still excludes it via AUTO_EXTRACTION_EXCLUDED_DOCUMENT_TYPES.
 */
export const WASTE_MODULE_ENRICHMENT_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.RCRAManifest,
  DocumentType.RCRAManifestPrintedCopy,
];

/**
 * The upload a Container-Tracking tenant can have read: the TSDF profile sheet,
 * which the profile extractor turns into a reviewable characterization.
 *
 * The generated summary (WasteStreamProfileSummary) is deliberately absent — it is
 * our own render of columns we already hold.
 */
export const CONTAINER_TRACKING_ENRICHMENT_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.WasteStreamProfile,
];

/**
 * The upload a tenant on the Manifests rework (manifest-views-v2) can have read:
 * the shipping document (bill of lading), which becomes a reviewable
 * shipping-document candidate.
 */
export const MANIFEST_VIEWS_V2_ENRICHMENT_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.WasteShippingDocument,
];

/**
 * Whether the pipeline would enrich this document for a tenant holding `flags`.
 *
 * `explicitRequest` distinguishes a request that names the document from a
 * tenant-wide or nightly run. Container-Tracking and manifest-views-v2 uploads
 * are admitted only when named: those tenants have not bought general
 * enrichment, so sweeping their uploads in would spend an extraction they did
 * not ask for.
 */
export function documentEnrichmentIsEntitled(
  documentType: DocumentType,
  flags: FeatureFlagMap,
  options: { explicitRequest: boolean }
): boolean {
  // DocumentAI buys enrichment for every document type.
  if (flags[FeatureFlagKey.DocumentAI]) {
    return true;
  }
  if (
    flags[FeatureFlagKey.WasteModule] &&
    WASTE_MODULE_ENRICHMENT_DOCUMENT_TYPES.includes(documentType)
  ) {
    return true;
  }
  if (
    options.explicitRequest &&
    flags[FeatureFlagKey.ContainerTracking] &&
    CONTAINER_TRACKING_ENRICHMENT_DOCUMENT_TYPES.includes(documentType)
  ) {
    return true;
  }
  if (
    options.explicitRequest &&
    flags[FeatureFlagKey.ManifestViewsV2] &&
    MANIFEST_VIEWS_V2_ENRICHMENT_DOCUMENT_TYPES.includes(documentType)
  ) {
    return true;
  }
  return false;
}
